import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import {
  Phone,
  Home,
  Clock,
  CheckCircle,
  DollarSign,
  ArrowRight,
  Shield,
  MapPin,
  Mail,
  Star,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Wrench,
  Users,
  FileText,
  Building,
  X,
} from 'lucide-react';

import BACKEND_URL from '../apiConfig';

const LandingPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', address: '', timeframe: 'within30days' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStep, setFormStep] = useState('form'); // 'form' | 'done'
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [utmParams, setUtmParams] = useState({});
  const [openFaq, setOpenFaq] = useState(null);
  const formRef = useRef(null);

  // Capture UTM + gclid on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utms = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid'].forEach(key => {
      const val = params.get(key);
      if (val) utms[key] = val;
    });
    setUtmParams(utms);
  }, []);

  // Exit intent detection (desktop: mouse leaves viewport top)
  useEffect(() => {
    if (sessionStorage.getItem('enko_exit_shown')) return;
    let timer;
    const handleMouseOut = (e) => {
      if (e.clientY <= 0 && formStep === 'form') {
        setShowExitPopup(true);
        sessionStorage.setItem('enko_exit_shown', '1');
        document.removeEventListener('mouseout', handleMouseOut);
      }
    };
    timer = setTimeout(() => {
      document.addEventListener('mouseout', handleMouseOut);
    }, 5000);
    return () => { clearTimeout(timer); document.removeEventListener('mouseout', handleMouseOut); };
  }, [formStep]);

  const trackEvent = (eventName, params = {}) => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, { ...params, ...utmParams });
    }
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handlePhoneClick = () => {
    // Phone click conversion is handled globally by GA4RouteTracker in App.js
  };

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/leads/`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        timeframe: formData.timeframe,
        source: 'website',
      });
      if (response.data.success) {
        trackEvent('generate_lead', { event_category: 'Lead Generation', event_label: 'Landing Page Form', value: 1 });
        setFormStep('done');
      }
    } catch {
      alert('Something went wrong. Please email us at alex@enkohomebuyers.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    { q: 'What is a subject-to mortgage takeover?', a: 'We take over your existing mortgage payments and you transfer the deed. Your loan stays current, your credit is protected, and you walk away free from the payment burden.' },
    { q: 'How fast can you close on my Florida home?', a: 'We can close in as little as 7-14 days. No waiting for bank financing, no inspections, no appraisals. We handle all the paperwork.' },
    { q: 'Do I need to make repairs before selling?', a: 'No. We buy homes in any condition — storm damage, outdated, roof issues, foundation problems. Sell as-is.' },
    { q: 'Do you charge fees or commissions?', a: 'Zero. No agent commissions, no closing costs, no hidden fees. The offer you accept is what you receive.' },
    { q: 'What if I owe more than my house is worth?', a: 'This is exactly where we specialize. We take over your mortgage payments through our subject-to program. No cash needed from you at closing.' },
    { q: 'Can I sell during foreclosure in Florida?', a: 'Yes. Florida uses judicial foreclosure which takes 6-12+ months. You likely still have time. Contact us immediately.' },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Enko Home Buyers",
    "description": "Cash home buyers in Sarasota, Charlotte County, and Southwest Florida. We specialize in mortgage takeovers, subject-to deals, and buying houses fast for cash.",
    "url": "https://www.enkohomebuyers.com",
    "email": "alex@enkohomebuyers.com",
    "areaServed": [
      { "@type": "State", "name": "Florida" },
      { "@type": "City", "name": "Sarasota" },
      { "@type": "City", "name": "North Port" },
      { "@type": "City", "name": "Venice" },
      { "@type": "City", "name": "Tampa" }
    ],
    "priceRange": "$$"
  };

  const painPoints = [
    { icon: AlertTriangle, title: 'Can\'t Sell on the Market?', desc: 'We buy homes that won\'t sell traditionally — low equity, needed repairs, or underwater mortgages.', link: '/sell-house-fast-sarasota-fl' },
    { icon: DollarSign, title: 'Behind on Payments?', desc: 'We take over your mortgage so you can stop the bleeding and walk away clean.', link: '/blog/behind-on-mortgage-payments-florida' },
    { icon: Building, title: 'Inherited Property', desc: "Don't want the house? Skip probate headaches — sell fast for cash.", link: '/blog/sell-inherited-house-florida' },
    { icon: Users, title: 'Tired Landlord', desc: 'Bad tenants, costly repairs? Sell your rental and move on — even with tenants in place.', link: '/sell-house-fast-tampa-fl' },
    { icon: Wrench, title: 'Storm Damage / Major Repairs', desc: 'Hurricane damage, roof issues, mold? We buy as-is. No repairs needed.', link: '/sell-house-fast-north-port-fl' },
  ];

  const testimonials = [
    { name: 'David R.', location: 'Sarasota, FL', text: 'They took over my mortgage payments when I couldn\'t sell on the market. Saved me from foreclosure.', rating: 5 },
    { name: 'James T.', location: 'Tampa, FL', text: 'Fair cash offer, no games. Closed in under two weeks on my inherited property.', rating: 5 },
    { name: 'Maria L.', location: 'North Port, FL', text: 'Was behind on payments and stressed. They handled everything and I walked away clean.', rating: 5 },
  ];

  // Reusable form JSX — NOT a component (avoids remount on every keystroke)
  const renderLeadForm = (id, refProp) => (
    <form onSubmit={handleSubmit} ref={refProp} className="space-y-3" data-testid={`lead-form-${id}`}>
      <div>
        <p className="text-xs text-gray-500 mb-1">Example: 1234 Fruitville Rd, Sarasota, FL 34236</p>
        <Input
          name="address"
          value={formData.address}
          onChange={handleInputChange}
          placeholder="Full Property Address (Street, City, State, Zip)"
          required
          className="h-12 text-base"
          data-testid={`form-address-${id}`}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          placeholder="Your Name"
          required
          className="h-12"
          data-testid={`form-name-${id}`}
        />
        <Input
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleInputChange}
          placeholder="Phone Number"
          required
          className="h-12"
          data-testid={`form-phone-${id}`}
        />
      </div>
      <Input
        name="email"
        type="email"
        value={formData.email}
        onChange={handleInputChange}
        placeholder="Email Address"
        required
        className="h-12"
        data-testid={`form-email-${id}`}
      />
      <select
        name="timeframe"
        value={formData.timeframe}
        onChange={handleInputChange}
        required
        className="w-full h-12 rounded-md border border-gray-200 px-3 text-sm bg-white"
        data-testid={`form-timeframe-${id}`}
      >
        <option value="within7days">Need to sell within 7 days</option>
        <option value="within30days">Need to sell within 30 days</option>
        <option value="30plusdays">30+ days / No rush</option>
      </select>
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-14 bg-[#22c55e] hover:bg-[#16a34a] text-white text-lg font-extrabold tracking-wide shadow-lg shadow-green-500/30 transition-all"
        data-testid={`form-submit-${id}`}
      >
        {isSubmitting ? 'Submitting...' : 'Get My Cash Offer'}
        <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
      <p className="text-xs text-center font-medium text-green-700" data-testid={`form-urgency-${id}`}>
        Cash offer in 24 hours &middot; No repairs &middot; No fees &middot; Close fast
      </p>
      <div className="pt-2 text-center border-t border-gray-100 mt-2" data-testid={`form-call-cta-${id}`}>
        <p className="text-xs text-gray-500 mb-1">Prefer to talk? Get your offer faster:</p>
        <a
          href="mailto:alex@enkohomebuyers.com"
         
          className="inline-flex items-center gap-2 text-green-700 hover:text-green-800 font-extrabold text-lg transition-colors"
          data-testid={`form-phone-link-${id}`}
        >
          <Phone className="w-5 h-5" />
          alex@enkohomebuyers.com
        </a>
      </div>
    </form>
  );

  // ── DONE: Thank you state ──
  if (formStep === 'done') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-2xl p-8 text-center shadow-2xl" data-testid="thank-you-card">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">We Got Your Info!</h2>
          <p className="text-gray-600 mb-4">We'll contact you within 24 hours with your cash offer.</p>
          <p className="text-sm text-gray-500">Or call us now:</p>
          <a href="mailto:alex@enkohomebuyers.com" className="text-xl font-bold text-green-600 hover:text-green-700" data-testid="thank-you-phone">
            alex@enkohomebuyers.com
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Can't Sell Your Florida Home? We Take Over Mortgage Payments | Enko Home Buyers</title>
        <meta name="description" content="Stuck with a home you can't sell in Sarasota or Charlotte County? We specialize in mortgage takeovers and cash offers for homes with little equity. No fees, close in 7-14 days." />
        <meta name="keywords" content="subject to mortgage Florida, can't sell house Florida, mortgage takeover Sarasota, cash home buyers Sarasota, sell house as-is Florida, we buy houses Charlotte County" />
        <link rel="canonical" href="https://www.enkohomebuyers.com/" />
        <meta property="og:title" content="Can't Sell Your Home? We Take Over Payments | Enko Home Buyers" />
        <meta property="og:description" content="Cash home buyers in Sarasota & Charlotte County. Subject-to mortgage takeovers. No repairs, no fees." />
        <meta property="og:url" content="https://www.enkohomebuyers.com/" />
      </Helmet>

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(businessSchema) }} />

      {/* ── HEADER ── */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <Home className="w-7 h-7 text-green-600" />
            <span className="text-xl font-bold text-gray-900">Enko Home Buyers</span>
          </Link>
          <a
            href="mailto:alex@enkohomebuyers.com"
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-full font-semibold text-sm hover:bg-green-700 transition-colors"
            data-testid="header-email-cta"
          >
            <Mail className="w-4 h-4" />
            <span className="hidden sm:inline">Email for Cash Offer</span>
            <span className="sm:hidden">Email Us</span>
          </a>
        </div>
      </header>

      {/* ── 1. HERO + ABOVE-THE-FOLD FORM ── */}
      <section className="bg-slate-900 text-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-14 lg:py-18">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
            <div className="lg:pr-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] mb-3 tracking-tight">
                Can't Sell Your Florida Home?{' '}
                <span className="text-green-400">We Take Over Your Mortgage Payments</span>
              </h1>

              <p className="text-sm sm:text-base text-slate-400 font-medium mb-2" data-testid="hero-trust-line">
                Serving Sarasota &middot; Charlotte County &middot; Southwest Florida
              </p>

              <p className="text-base sm:text-lg text-slate-300 mb-2 leading-relaxed">
                Behind on payments? Little or no equity? Can't sell on the market? We specialize in mortgage takeovers and cash offers for homes that won't sell the traditional way.
              </p>

              <p className="text-sm font-semibold text-green-400 mb-5" data-testid="hero-urgency-line">
                Cash Offer or Mortgage Takeover — No Obligation
              </p>

              <div className="bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-xl px-5 py-3 transition-colors inline-block" data-testid="hero-email-block">
                <p className="text-xs text-slate-400 mb-1 font-medium">Prefer to email? Reach out directly:</p>
                <a
                  href="mailto:alex@enkohomebuyers.com"
                  className="flex items-center gap-3"
                  data-testid="hero-email-link"
                >
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <span className="block text-white font-extrabold text-lg tracking-tight">alex@enkohomebuyers.com</span>
                    <span className="block text-slate-400 text-xs font-medium">We respond within 24 hours</span>
                  </div>
                </a>
              </div>

              <div className="flex flex-wrap gap-5 mt-6">
                {[
                  { icon: Clock, text: 'Close in 7 Days' },
                  { icon: DollarSign, text: 'Zero Fees' },
                  { icon: Shield, text: 'No Obligation' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                    <b.icon className="w-4 h-4 text-green-400" />
                    {b.text}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white text-gray-900 rounded-2xl p-5 md:p-7 shadow-2xl" data-testid="hero-form-card">
              <h2 className="text-xl font-bold text-center mb-1">Get Your Cash Offer Today</h2>
              <p className="text-sm text-gray-500 text-center mb-4">No obligation. Takes 30 seconds.</p>
              {renderLeadForm("hero", formRef)}
              <p className="text-xs text-gray-400 text-center mt-2">Your info is private. We never share your data.</p>

              <p className="text-xs text-center font-medium text-gray-500 mt-1" data-testid="hero-trust-micro">
                No spam. No pressure. 100% confidential.
              </p>

              {/* Inline reviews right under form */}
              <div className="mt-4 pt-4 border-t border-gray-100" data-testid="hero-inline-reviews">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider text-center mb-3">Trusted by Florida Homeowners</p>
                {[
                  { text: 'Alex made the process super easy and closed fast. No repairs needed.', name: 'Sarah M.', loc: 'Sarasota, FL' },
                  { text: 'Fair cash offer, no games. Closed in under two weeks on my inherited property.', name: 'James T.', loc: 'North Port, FL' },
                  { text: 'Was behind on payments and stressed. They took over my mortgage and I walked away clean.', name: 'Maria L.', loc: 'North Port, FL' },
                ].map((r, i) => (
                  <div key={i} className={`py-2.5 ${i > 0 ? 'border-t border-gray-50' : ''}`} data-testid={`hero-review-${i}`}>
                    <div className="flex gap-0.5 mb-1">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="w-3 h-3 text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 text-xs leading-relaxed">"{r.text}"</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">— {r.name}, {r.loc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. TRUST / CREDIBILITY ── */}
      <section className="py-12 bg-white border-b border-gray-100" data-testid="trust-section" style={{contentVisibility:"auto",containIntrinsicSize:"auto 300px"}}>
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8">
            Why Homeowners Choose Enko Home Buyers
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
            {[
              { icon: MapPin, text: 'Local Florida buyer — Sarasota, Charlotte County, SW Florida' },
              { icon: Wrench, text: 'No repairs or cleaning needed' },
              { icon: DollarSign, text: 'No agent commissions or hidden fees' },
              { icon: Clock, text: 'We close on your timeline' },
              { icon: CheckCircle, text: 'Cash offers within 24 hours' },
              { icon: Shield, text: 'Trusted by Florida homeowners' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`trust-item-${i}`}>
                <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-[18px] h-[18px] text-green-600" />
                </div>
                <span className="text-gray-700 font-medium text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. HOW IT WORKS ── */}
      <section className="py-14 bg-gray-50" data-testid="how-it-works-section" style={{contentVisibility:"auto",containIntrinsicSize:"auto 400px"}}>
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Submit Your Property', desc: 'Fill out the short form or call us. Takes less than a minute.' },
              { step: '2', title: 'Get a Cash Offer', desc: 'We evaluate your property and send a fair cash offer within 24 hours.' },
              { step: '3', title: 'Close & Get Paid', desc: 'Pick your closing date. We handle the paperwork. Get cash in as little as 7 days.' },
            ].map((item, i) => (
              <div key={i} className="text-center" data-testid={`step-${item.step}`}>
                <div className="w-14 h-14 rounded-full bg-green-600 text-white flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg shadow-green-600/30">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. SELLER PAIN POINTS ── */}
      <section className="py-14 bg-white" data-testid="pain-points-section" style={{contentVisibility:"auto",containIntrinsicSize:"auto 500px"}}>
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-3">
            We Help Homeowners in Difficult Situations
          </h2>
          <p className="text-center text-gray-500 mb-10 max-w-2xl mx-auto text-sm">
            No matter what you're going through, we provide fast, confidential cash offers so you can move forward.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {painPoints.map((item, i) => (
              <Link
                key={i}
                to={item.link}
                className="group block p-5 bg-gray-50 rounded-xl hover:bg-green-50 border border-gray-100 hover:border-green-200 transition-all"
                data-testid={`pain-point-${i}`}
              >
                <item.icon className="w-6 h-6 text-gray-400 group-hover:text-green-600 mb-3 transition-colors" />
                <h3 className="font-bold text-gray-900 mb-1 text-sm">{item.title}</h3>
                <p className="text-xs text-gray-600 leading-relaxed">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. MID-PAGE CTA ── */}
      <section className="py-12 bg-green-600" data-testid="mid-cta-section" style={{contentVisibility:"auto",containIntrinsicSize:"auto 250px"}}>
        <div className="max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">
            Get a Fair Cash Offer in 24 Hours
          </h2>
          <p className="text-green-100 mb-6 text-lg">No Repairs. No Fees. No Obligation.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={scrollToForm}
              className="bg-white text-green-700 hover:bg-green-50 font-bold text-lg px-8 h-12 shadow-lg"
              data-testid="mid-cta-offer-btn"
            >
              Get My Cash Offer <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <a
              href="mailto:alex@enkohomebuyers.com"
             
              className="flex items-center gap-2 text-white font-semibold text-lg hover:text-green-100 transition-colors"
              data-testid="mid-cta-phone"
            >
              <Phone className="w-5 h-5" />
              alex@enkohomebuyers.com
            </a>
          </div>
          <Link to="/mortgage-takeover-calculator" className="inline-flex items-center gap-2 mt-6 text-white underline underline-offset-4 font-semibold hover:text-green-100" data-testid="mid-cta-calculator-link">
            Little equity? Try our free Mortgage Takeover Calculator <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── 6. TESTIMONIALS ── */}
      <section className="py-14 bg-gray-50" data-testid="testimonials-section" style={{contentVisibility:"auto",containIntrinsicSize:"auto 400px"}}>
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10">
            What Our Sellers Say
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">"{t.text}"</p>
                <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                <p className="text-xs text-gray-500">{t.location}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. FAQ ── */}
      <section className="py-14 bg-white" data-testid="faq-section" style={{contentVisibility:"auto",containIntrinsicSize:"auto 600px"}}>
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 text-left font-medium text-gray-900 hover:bg-gray-50 transition-colors"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="pr-4">{faq.q}</span>
                  {openFaq === i ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 text-gray-600 text-sm leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 11. INTERNAL LINKS ── */}
      <section className="py-12 bg-gray-50" data-testid="internal-links-section" style={{contentVisibility:"auto",containIntrinsicSize:"auto 300px"}}>
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
            Florida Markets
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { to: '/sell-house-fast-sarasota-fl', label: 'Sell House Fast Sarasota, FL' },
              { to: '/sell-house-fast-tampa-fl', label: 'Sell House Fast Tampa, FL' },
              { to: '/sell-house-fast-north-port-fl', label: 'Sell House Fast North Port, FL' },
              { to: '/sell-house-fast-venice-fl', label: 'Sell House Fast Venice, FL' },
              { to: '/sell-house-fast-port-charlotte-fl', label: 'Sell House Fast Port Charlotte, FL' },
              { to: '/sell-house-fast-punta-gorda-fl', label: 'Sell House Fast Punta Gorda, FL' },
              { to: '/sell-house-fast-englewood-fl', label: 'Sell House Fast Englewood, FL' },
              { to: '/mortgage-takeover-calculator', label: 'Mortgage Takeover Calculator' },
              { to: '/blog/what-is-subject-to-mortgage-florida', label: 'What Is Subject-To?' },
              { to: '/blog', label: 'Read Our Blog' },
            ].map((link, i) => (
              <Link
                key={i}
                to={link.to}
                className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:border-green-300 hover:shadow-sm transition-all group"
                data-testid={`internal-link-${i}`}
              >
                <span className="font-medium text-gray-800 group-hover:text-green-700 transition-colors text-sm">{link.label}</span>
                <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8. FINAL CTA + FORM ── */}
      <section className="py-14 bg-slate-900 text-white" data-testid="final-cta-section" style={{contentVisibility:"auto",containIntrinsicSize:"auto 500px"}}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Get Your Cash Offer Today</h2>
          <p className="text-slate-400 mb-8">Fill out the form or call us — no obligation, no pressure.</p>
          <div className="max-w-md mx-auto bg-white text-gray-900 rounded-2xl p-6 shadow-xl">
            {renderLeadForm("bottom", null)}
          </div>
          <a
            href="mailto:alex@enkohomebuyers.com"
           
            className="inline-flex items-center gap-2 text-green-400 hover:text-green-300 font-semibold mt-6 text-lg transition-colors"
            data-testid="final-cta-phone"
          >
            <Phone className="w-5 h-5" />
            Email alex@enkohomebuyers.com
          </a>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-gray-950 text-white py-10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Home className="w-6 h-6 text-green-400" />
                <span className="text-lg font-bold">Enko Home Buyers</span>
              </div>
              <p className="text-sm text-gray-400">
                Cash home buyers serving Sarasota, Charlotte County, and Southwest Florida. Mortgage takeovers and cash offers. No repairs. No fees.
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-3 text-sm uppercase tracking-wider text-gray-300">Florida Markets</h5>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/sell-house-fast-sarasota-fl" className="hover:text-white transition-colors">Sell House Fast Sarasota, FL</Link></li>
                <li><Link to="/sell-house-fast-tampa-fl" className="hover:text-white transition-colors">Sell House Fast Tampa, FL</Link></li>
                <li><Link to="/sell-house-fast-north-port-fl" className="hover:text-white transition-colors">Sell House Fast North Port, FL</Link></li>
                <li><Link to="/sell-house-fast-venice-fl" className="hover:text-white transition-colors">Sell House Fast Venice, FL</Link></li>
                <li><Link to="/sell-house-fast-port-charlotte-fl" className="hover:text-white transition-colors">Sell House Fast Port Charlotte, FL</Link></li>
                <li><Link to="/sell-house-fast-punta-gorda-fl" className="hover:text-white transition-colors">Sell House Fast Punta Gorda, FL</Link></li>
                <li><Link to="/sell-house-fast-englewood-fl" className="hover:text-white transition-colors">Sell House Fast Englewood, FL</Link></li>
                <li><Link to="/mortgage-takeover-calculator" className="hover:text-white transition-colors text-green-400 font-medium">Mortgage Takeover Calculator</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors text-green-400 font-medium">Read Our Blog</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-3 text-sm uppercase tracking-wider text-gray-300">Contact</h5>
              <div className="space-y-2 text-sm text-gray-400">
                <a href="mailto:alex@enkohomebuyers.com" className="flex items-center gap-2 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" /> alex@enkohomebuyers.com
                </a>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Sarasota & Charlotte Counties, FL
                </div>
                <div className="flex items-center gap-3 mt-3">
                  <a href="https://www.facebook.com/profile.php?id=61578765569999" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" aria-label="Facebook">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <a href="https://www.instagram.com/enkohomebuyers/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" aria-label="Instagram">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  </a>
                </div>
              </div>
            </div>
            <div>
              <h5 className="font-semibold mb-3 text-sm uppercase tracking-wider text-gray-300">Hours</h5>
              <div className="space-y-1 text-sm text-gray-400">
                <p>Mon-Fri: 8am - 8pm</p>
                <p>Saturday: 9am - 6pm</p>
                <p>Sunday: 10am - 4pm</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-sm text-gray-500">
            <div className="flex justify-center gap-6 mb-3">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms & Conditions</Link>
            </div>
            <p>&copy; {new Date().getFullYear()} Enko Home Buyers. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ── EXIT INTENT POPUP ── */}
      {showExitPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="exit-popup-overlay">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl relative animate-in fade-in zoom-in-95 duration-200" data-testid="exit-popup">
            <button
              onClick={() => setShowExitPopup(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="exit-popup-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center mb-5">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Home className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Before you go...</h3>
              <p className="text-gray-500 text-sm">Want a quick cash offer? Takes 30 seconds.</p>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); setShowExitPopup(false); scrollToForm(); }} className="space-y-3">
              <Input
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Property Address"
                className="h-11"
                data-testid="exit-popup-address"
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your Name"
                  className="h-11"
                  data-testid="exit-popup-name"
                />
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Phone"
                  className="h-11"
                  data-testid="exit-popup-phone"
                />
              </div>
              <Button
                type="submit"
                onClick={(e) => {
                  e.preventDefault();
                  setShowExitPopup(false);
                  if (formData.address && formData.name && formData.phone) {
                    handleSubmit(e);
                  } else {
                    scrollToForm();
                  }
                }}
                className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-bold"
                data-testid="exit-popup-submit"
              >
                Get My Cash Offer <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
            <a
              href="mailto:alex@enkohomebuyers.com"
             
              className="flex items-center justify-center gap-2 text-gray-500 hover:text-green-600 text-sm mt-3 transition-colors"
              data-testid="exit-popup-phone"
            >
              <Phone className="w-3.5 h-3.5" />
              Email alex@enkohomebuyers.com
            </a>
          </div>
        </div>
      )}

      {/* ── 9. STICKY MOBILE CTA ── */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 flex gap-3 lg:hidden z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.1)]" data-testid="sticky-mobile-cta">
        <Button
          onClick={scrollToForm}
          className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold h-11"
          data-testid="sticky-get-offer-btn"
        >
          Get My Cash Offer
        </Button>
        <a
          href="mailto:alex@enkohomebuyers.com"
          className="flex items-center justify-center gap-2 bg-slate-900 text-white px-5 rounded-md font-semibold h-11 hover:bg-slate-800 transition-colors"
          data-testid="sticky-email-btn"
        >
          <Mail className="w-4 h-4" />
          Email Us
        </a>
      </div>
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
};

export default LandingPage;
